import { api, apiRequest } from "./api";
import type { PaginatedResponse } from "@/types/ping";
import type {
  MarkAllReadParams,
  MarkAllReadResponse,
  Nudge,
  NudgeCategoryMeta,
  NudgeListParams,
  NudgeUnreadCount,
} from "@/types/notifications";

/**
 * Nudges API — console surface.
 *
 * Backend is committed on Columbus `feat/nudges` but not merged or deployed,
 * so every call here fails until that lands.
 *
 * The console and the patient app are separate route trees, not one endpoint
 * that filters by who is asking: a console token gets a 401 on `/nudges/app/`
 * and vice versa. That is deliberate — the boundary is the route and the auth
 * class, never a rule the client is asked to apply.
 *
 * The recipient is always derived from the JWT; no method takes a user id and
 * none should be added. There is no RBAC gate — a nudge exists for one person.
 *
 * Contract: knowledge base, zarity/projects/nudges/contract.md
 */
/** Console surface. The patient tree (`/nudges/app/`) is the mobile app's. */
const BASE = "/nudges/console";

export const notificationsAPI = {
  /** GET /nudges/console/ — the feed, newest first by last_event_at. */
  list: (params?: NudgeListParams): Promise<PaginatedResponse<Nudge>> =>
    apiRequest(() => api.get(`${BASE}/`, { params })),

  /**
   * GET /nudges/console/categories/ — the category registry.
   *
   * Returns console categories only, so there is nothing to filter here. The
   * server decides what this surface may see, because a rule a client applies
   * is not a rule. Unpaginated: a short, complete enumeration.
   */
  categories: (): Promise<NudgeCategoryMeta[]> =>
    apiRequest(() => api.get(`${BASE}/categories/`)),

  /** GET /nudges/console/unread-count/ — unread_count is conversations, not messages. */
  unreadCount: (): Promise<NudgeUnreadCount> =>
    apiRequest(() => api.get(`${BASE}/unread-count/`)),

  /**
   * PATCH /nudges/console/{id}/read/ — idempotent.
   *
   * Returns 409 for a chat nudge, which clears only by opening the
   * conversation. Check `can_mark_read` on the object before calling.
   */
  markRead: (id: string): Promise<Nudge> =>
    apiRequest(() => api.patch(`${BASE}/${id}/read/`)),

  /**
   * POST /nudges/console/mark-all-read/ — skips chat nudges rather than refusing, and
   * reports skipped_count so we can explain a badge that stays lit.
   */
  markAllRead: (params?: MarkAllReadParams): Promise<MarkAllReadResponse> =>
    apiRequest(() => api.post(`${BASE}/mark-all-read/`, params ?? {})),
};
